import { Fab, Webchat } from '@botpress/webchat'
import { Outlet, createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { Boundary, Page, CodeSnippet, NavbarItem, BannerPortal } from '~/components'
import { AsyncButton, BpSpinner, EmptyState, Spinner } from '~/elements'
import { getIntegrationsFromBot } from '~/features/bots/services'
import { useCurrentRouteId, useIsAuthorized, useUpdateBot } from '~/hooks'
import { SidebarLayout } from '~/layouts'
import { getQueryOptions, useQuery, useSuspenseQuery } from '~/services'
import { config, getCdmStudioUrl } from '~/shared/configurations'
import { titleCase } from '~/utils'
import semver from 'semver'
import { useWebchatEmbedScript } from '~/hooks/webchat'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { MessageSquareOff, TriangleAlert, TriangleAlertIcon } from 'lucide-react'
import { AlertTitle, Button, Card, ColoredAlert, showConfirmationPrompt } from '@bpinternal/ui-kit-next'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useFeature } from '~/hooks'

const currentRoute = '/workspaces/$workspaceId/bots/$botId/webchat' as const
const LATEST_WEBCHAT_VERSION = '0.3.0'

export const Route = createFileRoute(currentRoute)({
  component: () => <Component />,
})

function Component() {
  const { botId, workspaceId } = Route.useParams()
  const { mutate: updateBot, isPending: isPendingUpdateBot } = useUpdateBot()

  const [
    {
      data: { integration: latestWebchatIntegration },
    },
    { data: bot },
  ] = useSuspenseQueries({
    queries: [
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_/integrations/$integrationName/$version', {
        workspaceId,
        botId,
        integrationName: 'webchat',
        version: 'latest',
      }),
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', { workspaceId, botId }),
    ],
  })

  const webchatIntegration = Object.values(bot.integrations).find((integration) => integration.name === 'webchat')
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [])
  const _hasHydrated = useConfigStore((state) => state._hasHydrated)

  const { embedScript } = useWebchatEmbedScript({ botId, workspaceId })

  const sidebarMenu = ['general', 'theme', 'share', 'advanced-settings'] as const
  const location = useCurrentRouteId() ?? ''

  const items: NavbarItem[] = sidebarMenu.map((item) => ({
    children: titleCase(item),
    to: `/workspaces/$workspaceId/bots/$botId/webchat/v2/${item}`,
    active: location.includes(item),
  }))

  if (!webchatIntegration) {
    return (
      <Page title={'Webchat'}>
        <div className="p-8">
          <EmptyState
            title="Webchat not installed"
            description="You must install the Webchat integration to interact with it."
            primaryAction={
              <Button
                onClick={() =>
                  updateBot({
                    id: bot.id,
                    workspaceId,
                    integrations: { [latestWebchatIntegration.id]: { enabled: true, configuration: {} } },
                  })
                }
              >
                Install Webchat
              </Button>
            }
          />
        </div>
      </Page>
    )
  }

  const handleUpgrade = async () => {
    const result = await showConfirmationPrompt(
      <div className="flex flex-col gap-2">
        <span className="text-sm text-muted-foreground">
          Please copy and paste the following code on your webpage before upgrading:
        </span>
        <CodeSnippet code={embedScript ?? ''} />
      </div>,
      { title: 'Upgrade Webchat', showCancel: true, confirmLabel: 'Upgrade', size: 'max-w-4xl' }
    )

    if (result) {
      updateBot({
        id: bot.id,
        workspaceId,
        integrations: { [latestWebchatIntegration.id]: { enabled: true } },
      })
    }
  }

  return (
    <>
      <Page
        title={
          <div className="flex items-center gap-2">
            <span>Webchat</span>
            {latestWebchatIntegration.version !== webchatIntegration?.version && (
              <>
                <BannerPortal>
                  <ColoredAlert color="amber">
                    <TriangleAlertIcon className="size-5" />
                    <AlertTitle>
                      You're using an older version of Webchat that will no longer function as of{' '}
                      <strong>Dec 31, 2025</strong>. Click{' '}
                      <strong
                        className="cursor-pointer hover:underline"
                        onClick={() => {
                          if (isPendingUpdateBot) return
                          handleUpgrade()
                        }}
                      >
                        Upgrade{' '}
                      </strong>
                      to generate a new embed snippet and replace your existing Webchat code. After upgrading, your
                      Webchat will be unavailable until you replace the deprecated embed snippet.
                    </AlertTitle>
                  </ColoredAlert>
                </BannerPortal>
                <AsyncButton size="sm" loading={isPendingUpdateBot} onClick={handleUpgrade}>
                  Upgrade
                </AsyncButton>
              </>
            )}
          </div>
        }
        navbarItems={items}
      >
        {_hasHydrated ? (
          <SidebarLayout
            main={
              <div className="max-w-xl">
                <Outlet />
              </div>
            }
            rightSidebarSize="5"
            rightSidebarClassName="2xl:mr-36"
            rightSidebar={
              <Boundary>
                <RightSidebar />
              </Boundary>
            }
          />
        ) : (
          <div className="mx-auto size-20 h-96">
            <BpSpinner />
          </div>
        )}
      </Page>
    </>
  )
}

const RightSidebar = () => {
  const { workspaceId, botId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const bot = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId }).data

  const { data: script, isError } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/webchat', {
    botId,
    workspaceId,
    type: 'fullscreen',
  })

  const { mutate: updateBot } = useUpdateBot()

  const webchatInstalledIntegration = Object.values(bot.integrations).find(
    (integration) => integration.name === 'webchat'
  )

  const isAuthorized = useIsAuthorized({ workspaceId, userId: user.id })
  const isAuthorizedToOpenStudio = isAuthorized('studio.view') || isAuthorized('studio.edit')

  const webchatIntegration = getIntegrationsFromBot(bot).find((integration) => integration.name === 'webchat')
  const isLatestWebchatVersion = semver.gte(webchatIntegration?.version ?? '0.0.0', LATEST_WEBCHAT_VERSION)

  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [])

  const botName = useConfigStore((state) => state.botName)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const botDescription = useConfigStore((state) => state.botDescription)
  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const soundEnabledInConfig = useConfigStore((state) => state.soundEnabled)

  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const fabImage = useConfigStore((state) => state.fabImage)
  const footer = useConfigStore((state) => state.footer)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const email = useConfigStore((state) => state.email)
  const phone = useConfigStore((state) => state.phone)
  const termsOfService = useConfigStore((state) => state.termsOfService)
  const privacyPolicy = useConfigStore((state) => state.privacyPolicy)
  const website = useConfigStore((state) => state.website)
  const themeMode = useConfigStore((state) => state.themeMode)
  const radius = useConfigStore((state) => state.radius)
  const color = useConfigStore((state) => state.color)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const variant = useConfigStore((state) => state.variant)
  const customStyleSheet = useConfigStore((state) => state.customStyleSheet)

  const pushpinUpgrade = useFeature('pushpin-upgrade')

  if (!(bot.deployedAt || bot.dev) || !webchatIntegration) {
    return (
      <Card className="flex h-200 items-center px-8">
        <EmptyState
          iconSize={3}
          title={'Chatbot not published'}
          description={'You must publish the chatbot from Botpress Studio to be able to interact with it.'}
          primaryAction={
            isAuthorizedToOpenStudio ? (
              <a href={getCdmStudioUrl(bot.id)} target="_blank" rel="noopener noreferrer">
                <Button disabled={!isAuthorizedToOpenStudio}>Edit in studio</Button>
              </a>
            ) : undefined
          }
        />
      </Card>
    )
  }

  if (!isLatestWebchatVersion) {
    return (
      <Card className="flex h-200 items-center px-8">
        <EmptyState
          iconSize={3}
          title={'Webchat integration outdated'}
          description={'You must upgrade the webchat integration to interact with it in the Dashboard.'}
        />
      </Card>
    )
  }

  function enableWebchat(webchatIntegrationId: string) {
    updateBot({
      id: bot.id,
      workspaceId,
      integrations: { [webchatIntegrationId]: { enabled: true, configuration: {} } },
    })
  }

  if (!(script && webchatIntegration) && !isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!webchatIntegration?.enabled) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <EmptyState
          icon={MessageSquareOff}
          title="Webchat disabled"
          description="You must enable the Webchat integration to interact with it."
        />
        <div className="flex flex-1">
          <div>
            <Button onClick={() => enableWebchat(webchatIntegration.id)}>Enable Webchat</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-200 flex-col gap-4">
      <Webchat
        configuration={{
          botName,
          botAvatar,
          botDescription,
          feedbackEnabled,
          soundEnabled: soundEnabledInConfig,
          composerPlaceholder,
          footer,
          storageLocation,
          email,
          phone,
          website,
          privacyPolicy,
          termsOfService,
          radius,
          color,
          fontFamily,
          variant,
          headerVariant,
          themeMode,
        }}
        allowFileUpload={allowFileUpload}
        apiUrl={pushpinUpgrade ? 'https://pushpin.botpress.cloud' : config.webchatUrl}
        clientId={webchatInstalledIntegration?.webhookId ?? ''}
        storageKey={`bp-webchat-${botId}`}
      />
      <div className="ml-auto h-16 w-16 flex-none">
        <Fab imgUrl={fabImage} />
      </div>
      <style>{customStyleSheet}</style>
    </div>
  )
}
